import { useMemo } from 'react';
import {
  checkMachiningOptions,
  isBaseApiSeqnoGroup,
  findApiSeqnoFromToleranceCode,
  filterOptionsByGroup,
  ToleranceActiveGroup,
} from 'src/utils/tolerance';

type WatchedToleranceValues = {
  thickness?: string;
  width?: string;
  length?: string;
  outside?: string;
};

type UseToleranceGroupFilterParams = {
  product: string;
  toleranceOptions: Response.ToleranceGroupOptions;
  watchedValues: WatchedToleranceValues;
};

type UseToleranceGroupFilterReturn = {
  baseOptions: {
    thickness: Response.ToleranceOption[];
    width: Response.ToleranceOption[];
    length: Response.ToleranceOption[];
    outside: Response.ToleranceOption[];
  };
  filteredOptions: {
    thickness: Response.ToleranceOption[];
    width: Response.ToleranceOption[];
    length: Response.ToleranceOption[];
    outside: Response.ToleranceOption[];
  };
  activeGroup: ToleranceActiveGroup;
};

/**
 * Custom hook to handle tolerance options group filtering.
 * Groups tolerance options into "base" (apiSeqno 0, 1) and "api" (other apiSeqno).
 * When user selects a tolerance, all dropdowns show only options from the same group.
 */
export const useToleranceGroupFilter = ({
  product,
  toleranceOptions,
  watchedValues,
}: UseToleranceGroupFilterParams): UseToleranceGroupFilterReturn => {
  // Get base options (before group filtering)
  const baseOptions = useMemo(
    () => ({
      thickness: checkMachiningOptions(
        product,
        toleranceOptions?.thickness,
        toleranceOptions?.apiCalled,
      ),
      width: checkMachiningOptions(
        product,
        toleranceOptions?.width,
        toleranceOptions?.apiCalled,
      ),
      length: checkMachiningOptions(
        product,
        toleranceOptions?.length,
        toleranceOptions?.apiCalled,
      ),
      outside: checkMachiningOptions(
        product,
        toleranceOptions?.outsideDia,
        toleranceOptions?.apiCalled,
      ),
    }),
    [product, toleranceOptions],
  );

  // Determine active group based on any selected tolerance
  const activeGroup = useMemo((): ToleranceActiveGroup => {
    const allOptions = [
      { value: watchedValues.thickness, options: baseOptions.thickness },
      { value: watchedValues.width, options: baseOptions.width },
      { value: watchedValues.length, options: baseOptions.length },
      { value: watchedValues.outside, options: baseOptions.outside },
    ];

    for (const { value, options } of allOptions) {
      if (value) {
        const apiSeqno = findApiSeqnoFromToleranceCode(options, value);
        if (apiSeqno !== undefined) {
          return isBaseApiSeqnoGroup(apiSeqno) ? 'base' : 'api';
        }
      }
    }
    return null;
  }, [watchedValues, baseOptions]);

  // Filter options based on active group
  const filteredOptions = useMemo(
    () => ({
      thickness: filterOptionsByGroup(baseOptions.thickness, activeGroup),
      width: filterOptionsByGroup(baseOptions.width, activeGroup),
      length: filterOptionsByGroup(baseOptions.length, activeGroup),
      outside: filterOptionsByGroup(baseOptions.outside, activeGroup),
    }),
    [baseOptions, activeGroup],
  );

  return {
    baseOptions,
    filteredOptions,
    activeGroup,
  };
};

export default useToleranceGroupFilter;
